import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getPotentialAnalysisSetting,
  updatePotentialAnalysisSetting,
} from '@/lib/api/potential-analysis-setting';
import { getProductGroupLevelCounts } from '@/lib/api/product-group-type';
import { getSellerHierarchies } from '@/lib/api/seller-hierarchy';
import { Button } from '@/components/ui/button';
import type { ProductGroupLevelCounts, SimpleAccount } from '@/lib/types';
import {
  CROSS_DEFAULT_PRODUCT_GROUP_LEVEL,
  DEFAULT_INACTIVE_DAYS,
  DEFAULT_PRODUCT_GROUP_LEVEL,
  EDITABLE_CROSS_SETS,
  MAX_INACTIVE_DAYS,
  MIN_INACTIVE_DAYS,
  type PotentialAnalysisCrossSetSetting,
  type PotentialAnalysisProductGroupTypeSetting,
} from '@/lib/types/feature/potential-analysis-setting';
import { CrossAxisSection } from './potential-analysis/CrossAxisSection';
import { EstimateNote } from './potential-analysis/EstimateNote';
import { emptyEditState, toEditState, type EditState } from './potential-analysis/editState';
import {
  ProductGroupTypeTable,
  type TypeSelection,
} from './potential-analysis/ProductGroupTypeTable';
import {
  estimateRows,
  groupCountOf,
  ESTIMATED_ROWS_WARN_THRESHOLD,
  type CrossAxisDraft,
} from './potential-analysis/estimate';

interface PotentialAnalysisSettingProps {
  account: SimpleAccount;
}

/**
 * ポテンシャル分析設定（FKV3-13111 / 掛け合わせは FKV3-13122）
 *
 * 週次実績の事前集計（monet-rust の weekly_performance_* モジュール）が読む設定を編集する。
 * 抽出タブは実行のたびに条件を画面入力するが、事前集計では固定値が要るため、
 * アカウントごとに1つ決めて持つ。
 *
 * タイプごとのグループ数と推定行数を出すのは、何も示さなければ最下層が選ばれ続け、
 * 毎日作り直すテーブルが膨らむため（管理画面の利用実態では実質最下層が85%）。
 */
export function PotentialAnalysisSetting({ account }: PotentialAnalysisSettingProps) {
  const { getAuthHeader } = useAuthHeader();

  const [types, setTypes] = useState<PotentialAnalysisProductGroupTypeSetting[]>([]);
  /** 保存済みの掛け合わせ。軸に残ったマスタ欠落タイプを画面へ出すために持つ */
  const [crossSets, setCrossSets] = useState<PotentialAnalysisCrossSetSetting[]>([]);
  const [edit, setEdit] = useState<EditState>(emptyEditState);
  const [saved, setSaved] = useState<EditState>(emptyEditState);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /**
   * 取得の失敗。編集フォームを出さずにこれだけ見せる。
   *
   * 取得できていないのにフォームを出すと、空欄の入力エラーが並んで「入力を直せば保存できる」
   * ように見えてしまう（実際には保存できない）。
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** 保存の失敗。編集中の内容を失わせないよう、フォームは出したまま上部に見せる */
  const [saveError, setSaveError] = useState<string | null>(null);
  /** 再読み込みの契機。タブを開き直さずに復帰できるようにする */
  const [reloadKey, setReloadKey] = useState(0);

  /** 階層別グループ数。設定本体より重い（Athena）ので別に取り、失敗しても設定は編集できる */
  const [levelCounts, setDepthCounts] = useState<ProductGroupLevelCounts[] | null>(null);
  const [levelCountsLoading, setDepthCountsLoading] = useState(true);
  /** 全社行を含む行数見積もり用の店舗数。取れなければ行数を出さない */
  const [sellerCount, setSellerCount] = useState<number | null>(null);

  // アカウント切替時に状態を捨てる（前アカウントの設定が残ると誤保存につながる）
  const accountCodeRef = useRef(account.code);
  if (account.code !== accountCodeRef.current) {
    accountCodeRef.current = account.code;
    setTypes([]);
    setCrossSets([]);
    setEdit(emptyEditState());
    setSaved(emptyEditState());
    setDepthCounts(null);
    setSellerCount(null);
    setLoadError(null);
    setSaveError(null);
    setLoading(true);
    setDepthCountsLoading(true);
    setSaving(false);
  }

  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    const authHeaders = getAuthHeader();

    setLoading(true);
    getPotentialAnalysisSetting(account.code, authHeaders)
      .then((setting) => {
        if (cancelled) return;
        const state = toEditState(
          setting.product_group_types,
          setting.product_group_types,
          setting.cross_sets,
          setting.inactive_days,
        );
        setTypes(setting.product_group_types);
        setCrossSets(setting.cross_sets);
        setEdit(state);
        setSaved(state);
        setLoadError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'ポテンシャル分析設定の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // グループ数と店舗数は見積もり表示だけに使う。失敗しても設定の編集は止めない
    setDepthCountsLoading(true);
    Promise.allSettled([
      getProductGroupLevelCounts(account.code, authHeaders),
      getSellerHierarchies(account.code, authHeaders),
    ])
      .then(([counts, sellers]) => {
        if (cancelled) return;
        if (counts.status === 'fulfilled') setDepthCounts(counts.value);
        if (sellers.status === 'fulfilled') setSellerCount(sellers.value.length);
      })
      .finally(() => {
        if (!cancelled) setDepthCountsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [account.code, reloadKey]);

  const inactiveDaysError = useMemo(() => {
    const value = Number(edit.inactiveDays);
    if (edit.inactiveDays.trim() === '' || !Number.isInteger(value)) {
      return '整数で入力してください';
    }
    if (value < MIN_INACTIVE_DAYS || value > MAX_INACTIVE_DAYS) {
      return `${MIN_INACTIVE_DAYS}〜${MAX_INACTIVE_DAYS}日で入力してください`;
    }
    return null;
  }, [edit.inactiveDays]);

  const isDirty = useMemo(() => JSON.stringify(edit) !== JSON.stringify(saved), [edit, saved]);

  /**
   * 保存できるか。
   *
   * 画面が編集できる数を超える組があるときは、変更が無くても保存させる。保存が組を
   * 整理する操作であり、変更を求めると「直すために関係ない項目をいじる」ことになるため。
   */
  const hasUneditableSets = crossSets.length > EDITABLE_CROSS_SETS;
  const canSave = (isDirty || hasUneditableSets) && !saving && inactiveDaysError === null;

  /** 縦持ちの行数見積もり（マスタ全体を母数にした上限）。母数が欠けていれば null */
  const estimate = useMemo(() => {
    if (levelCounts === null || sellerCount === null) return null;
    const enabled = types.filter((t) => edit.selections[t.product_group_type_id]?.enabled);
    if (enabled.length === 0) return { groups: 0, rows: 0 };
    let groups = 0;
    for (const t of enabled) {
      const count = groupCountOf(
        levelCounts.find((c) => c.type_id === t.product_group_type_id),
        edit.selections[t.product_group_type_id]?.productGroupLevel ?? DEFAULT_PRODUCT_GROUP_LEVEL,
      );
      // グループ数が取れないタイプがあると過小な見積もりになるため、見積もり自体をやめる
      if (count === null) return null;
      groups += count;
    }
    return { groups, rows: estimateRows(groups, sellerCount) };
  }, [levelCounts, sellerCount, types, edit.selections]);

  /** 画面が編集する組の軸のうち、マスタに無いタイプ。選択肢に出して外せるようにする */
  const missingCrossAxes = useMemo(
    () => (crossSets[0]?.axes ?? []).filter((a) => a.missing_from_master),
    [crossSets],
  );

  /** 警告に出すマスタ欠落タイプのID。縦持ちと掛け合わせの双方から集める */
  const missingTypeIds = useMemo(() => {
    const ids = types.filter((t) => t.missing_from_master).map((t) => t.product_group_type_id);
    for (const axis of missingCrossAxes) {
      if (!ids.includes(axis.product_group_type_id)) ids.push(axis.product_group_type_id);
    }
    return ids;
  }, [types, missingCrossAxes]);

  const setSelection = (typeId: string, next: Partial<TypeSelection>) => {
    setEdit((prev) => ({
      ...prev,
      selections: {
        ...prev.selections,
        [typeId]: {
          enabled: prev.selections[typeId]?.enabled ?? false,
          productGroupLevel: prev.selections[typeId]?.productGroupLevel ?? null,
          ...next,
        },
      },
    }));
  };

  const setCrossAxis = (index: number, next: CrossAxisDraft) => {
    setEdit((prev) => {
      const crossAxes = [...prev.crossAxes];
      const wasEmpty = crossAxes[index].productGroupTypeId === null;
      crossAxes[index] = {
        productGroupTypeId: next.productGroupTypeId,
        // 未選択の軸にタイプを入れたときは既定の階層から始める（行数が積で増えるため浅く）
        productGroupLevel:
          wasEmpty && next.productGroupTypeId !== null
            ? CROSS_DEFAULT_PRODUCT_GROUP_LEVEL
            : next.productGroupLevel,
      };
      return { ...prev, crossAxes };
    });
  };

  const handleSave = async () => {
    // 保存はこのアカウントに対する操作。完了までにアカウントが切り替わったら結果を
    // 画面へ書き戻さない（PUT は全置換のため、書き戻すと次の保存で他アカウントを上書きする）
    const requestCode = account.code;
    // 既にある組は引き継ぎ、無ければ採番する
    const crossSetId = edit.crossSetId ?? crypto.randomUUID();
    const crossAxes = edit.crossAxes
      .filter((a) => a.productGroupTypeId !== null)
      .map((a) => ({
        product_group_type_id: a.productGroupTypeId as string,
        product_group_level: a.productGroupLevel,
      }));
    try {
      setSaving(true);
      setSaveError(null);
      const result = await updatePotentialAnalysisSetting(
        requestCode,
        {
          product_group_types: types
            .filter((t) => edit.selections[t.product_group_type_id]?.enabled)
            .map((t) => ({
              product_group_type_id: t.product_group_type_id,
              product_group_level:
                edit.selections[t.product_group_type_id]?.productGroupLevel ??
                DEFAULT_PRODUCT_GROUP_LEVEL,
            })),
          // 軸を選んだ順に詰める。DB は「軸2が空なら軸3も空」を要求するため空きを残さない。
          // 画面は1組だけを編集するので、送るのも1組（軸が無ければ組ごと送らない）
          cross_sets: crossAxes.length === 0 ? [] : [{ id: crossSetId, axes: crossAxes }],
          inactive_days: Number(edit.inactiveDays),
        },
        getAuthHeader(),
      );
      toast.success(`${requestCode} のポテンシャル分析設定を保存しました`);
      if (accountCodeRef.current !== requestCode) return;
      // マスタを引けなかった応答は設定済みのタイプしか含まないため、一覧は差し替えない。
      // ただしマスタに無いタイプは設定行が消えた時点で画面からも落とす（消したのに警告が残らないよう）
      const savedIds = new Set(result.product_group_types.map((t) => t.product_group_type_id));
      const rows = result.master_available
        ? result.product_group_types
        : types.filter((t) => !t.missing_from_master || savedIds.has(t.product_group_type_id));
      const state = toEditState(
        rows,
        result.product_group_types,
        result.cross_sets,
        result.inactive_days,
      );
      setTypes(rows);
      setCrossSets(result.cross_sets);
      setEdit(state);
      setSaved(state);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'ポテンシャル分析設定の保存に失敗しました';
      toast.error(`${requestCode}: ${message}`);
      if (accountCodeRef.current !== requestCode) return;
      setSaveError(message);
    } finally {
      if (accountCodeRef.current === requestCode) setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ポテンシャル分析設定
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          週次実績の事前集計（ポテンシャル分析向けのAthena出力）で使う設定です。抽出タブのように
          実行のたびに指定するのではなく、アカウントごとに1つ決めて持ちます。選んだ商品グループ
          タイプだけが出力され、翌日の日次集計から反映されます。
        </Typography>

        {saveError && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </MuiAlert>
        )}

        {hasUneditableSets && (
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            掛け合わせの設定が{crossSets.length}組あります。画面で編集できるのは
            {EDITABLE_CROSS_SETS}組までです。保存すると編集中の組だけが残り、他の組は消えます。
          </MuiAlert>
        )}

        {missingTypeIds.length > 0 && (
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            設定に残っていますが商品グループマスタに存在しないタイプがあります（
            {missingTypeIds.join(', ')}
            ）。集計ジョブは読み飛ばします。選択を外して保存すると設定から消えます。
          </MuiAlert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>設定を読み込み中...</Typography>
          </Box>
        ) : loadError !== null ? (
          // 取得できていないので編集フォームは出さない。空欄の入力エラーが並ぶと
          // 「入力を直せば保存できる」ように見えてしまう
          <MuiAlert
            severity="error"
            action={
              <Button variant="outline" size="sm" onClick={() => setReloadKey((n) => n + 1)}>
                再読み込み
              </Button>
            }
          >
            設定を取得できませんでした: {loadError}
          </MuiAlert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              出力する商品グループタイプ
            </Typography>
            <ProductGroupTypeTable
              types={types}
              selections={edit.selections}
              onChange={setSelection}
              levelCounts={levelCounts}
              levelCountsLoading={levelCountsLoading}
            />

            {/* 行数が膨らむ前に気づけるよう、保存前に見積もりを出す（設計 D10） */}
            {estimate !== null && (
              <EstimateNote
                warning={estimate.rows >= ESTIMATED_ROWS_WARN_THRESHOLD}
                headline={`選択中の合計 ${estimate.groups.toLocaleString()} グループ → 推定 約${estimate.rows.toLocaleString()} 行／2年`}
                warningDetail="毎日作り直すテーブルとしては大きい規模です。集計階層を浅くすると大きく減ります。"
                note="（マスタ全体を母数にした上限。実際の行数はこれより小さくなります）"
                marginBottom={3}
              />
            )}

            <CrossAxisSection
              types={types}
              axes={edit.crossAxes}
              onChange={setCrossAxis}
              missingAxes={missingCrossAxes}
              levelCounts={levelCounts}
              levelCountsLoading={levelCountsLoading}
              sellerCount={sellerCount}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              非稼働判定日数
            </Typography>
            <TextField
              size="small"
              type="number"
              value={edit.inactiveDays}
              onChange={(e) => setEdit((prev) => ({ ...prev, inactiveDays: e.target.value }))}
              error={inactiveDaysError !== null}
              helperText={
                inactiveDaysError ??
                `各週末から遡ってこの日数に販売が無い倉庫×SKUを非稼働在庫とします（${MIN_INACTIVE_DAYS}〜${MAX_INACTIVE_DAYS}日・既定${DEFAULT_INACTIVE_DAYS}日）`
              }
              inputProps={{
                'aria-label': '非稼働判定日数',
                min: MIN_INACTIVE_DAYS,
                max: MAX_INACTIVE_DAYS,
              }}
              sx={{ width: 320 }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 3,
                gap: 2,
              }}
            >
              {/* 設定テーブルが更新日時を持たなくなったため、最終更新は表示しない */}
              <Box />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {isDirty && (
                  <Typography variant="caption" className="text-amber-600">
                    未保存の変更があります
                  </Typography>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!isDirty || saving}
                  onClick={() => setEdit(saved)}
                >
                  元に戻す
                </Button>
                <Button size="sm" disabled={!canSave} onClick={handleSave}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  保存
                </Button>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
